import { escapeHtml } from '../ui.js';

// The enum pill — edit-patterns artboard 6e. A value from a fixed short list that
// OTHER screens read (status, stage, permission), so changing one moves other
// people's screens. The pill is the affordance; its menu shows the CONSEQUENCE of
// each option in grey underneath; and where the change alters someone else's work
// it asks for a REASON before committing.
//
// Geometry is verbatim from 6e:
//   Pill      42px tall, 1px --brand-blue border, 999px radius, value 13.5px/500
//             --brand-dark-blue, caret 11px --brand-blue. OUTLINE, NEVER FILLED (a
//             filled pill in Track means a chosen filter). Open adds
//             --brand-blue-50 and flips the caret (▾ → ▴).
//   Menu      300px, 12px radius, 1px --brand-grey-200, shadow
//             0 12px 32px rgba(12,17,57,0.14) (one of only two shadows in the app),
//             padding 7px 0. Options 9px 14px, node + label 13.5px + consequence
//             11.5px grey. CURRENT value takes the current-node mark (filled
//             --brand-blue in an --brand-blue-100 ring, row on --brand-blue-50). Options
//             that END something sit LAST, behind a hairline, with an
//             orange-outline node.
//   Reason    bordered card, radius 12, 18px 20px 20px: title 15px/500, one-line
//             consequence sentence 12.5px grey, a REQUIRED reason field labelled
//             "Reason — goes on the thread", and buttons (commit blue / Cancel).
//             The reason sentence is what the change list shows.
//
// LAWS (do not undo): the pill is outline, never filled. The ending option is last,
// behind a hairline, with an ORANGE node — nothing here is red. A plain status
// change commits immediately; only changes that touch someone else's work (hold,
// close, void, permission, former) take the reason step. A role-gated pill stays
// VISIBLE with the holding role named at the right in 10px caps — never a disabled
// grey control.
//
// This component does NOT do the write. The caller's onChange does the write and
// the change-list line, so the pill stays reusable for status / stage / permission.

let openEl = null;      // the floating panel currently open (menu OR reason card)
let openAnchor = null;  // the pill it belongs to (excluded from outside-click)

function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); closePanel(); } }
function onDoc(e) {
  if (!openEl) return;
  if (openEl.contains(e.target)) return;
  if (openAnchor && openAnchor.contains(e.target)) return; // the pill toggles itself
  closePanel();
}

function closePanel() {
  if (!openEl) return;
  const anchor = openAnchor;
  openEl.remove();
  openEl = null;
  openAnchor = null;
  document.removeEventListener('keydown', onKey, true);
  document.removeEventListener('click', onDoc, true);
  if (anchor && typeof anchor._setOpen === 'function') anchor._setOpen(false);
}

function registerOpen(panel, anchor) {
  openEl = panel;
  openAnchor = anchor;
  // Bind on the NEXT tick so the opening click doesn't immediately close it.
  setTimeout(() => {
    if (openEl !== panel) return;
    document.addEventListener('click', onDoc, true);
    document.addEventListener('keydown', onKey, true);
  }, 0);
}

function positionUnder(el, anchor, width) {
  const r = anchor.getBoundingClientRect();
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const left = Math.min(Math.max(8, Math.round(r.left)), maxLeft);
  Object.assign(el.style, {
    position: 'fixed',
    top: `${Math.round(r.bottom + 6)}px`,
    left: `${left}px`,
  });
}

export function renderPill(container, { value, options, onChange, disabled, gateRole } = {}) {
  container.innerHTML = '';
  const opts = Array.isArray(options) ? options : [];
  let current = value;

  const labelFor = (v) => {
    const o = opts.find((x) => x.value === v);
    return o ? o.label : (v == null ? '' : String(v));
  };

  const wrap = document.createElement('span');
  wrap.style.cssText = 'display:inline-flex;align-items:center;gap:10px;';

  const pill = document.createElement('span');

  // ---- disabled / role-gated: an inert pill with the holding role named, never a
  //      greyed-out disabled control (6e / section 6 role gating) --------------
  if (disabled) {
    pill.style.cssText =
      'height:42px;display:inline-flex;align-items:center;padding:0 18px;'
      + 'border:1px solid var(--link);border-radius:999px;'
      + 'font-size:13.5px;font-weight:500;color:var(--anchor);cursor:default;';
    pill.textContent = labelFor(current);
    wrap.appendChild(pill);
    if (gateRole) {
      const tag = document.createElement('span');
      tag.style.cssText =
        'font-size:10px;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--ink-3);';
      tag.textContent = gateRole;
      wrap.appendChild(tag);
    }
    container.appendChild(wrap);
    return;
  }

  // ---- interactive pill -------------------------------------------------------
  const paintPill = (open) => {
    pill.style.cssText =
      'height:42px;display:inline-flex;align-items:center;gap:12px;padding:0 18px;'
      + 'border:1px solid var(--link);border-radius:999px;'
      + 'font-size:13.5px;font-weight:500;color:var(--anchor);cursor:pointer;'
      + (open ? 'background:var(--nav-hover);' : '');
    const caret = open ? '&#9652;' : '&#9662;';
    pill.innerHTML =
      `<span>${escapeHtml(labelFor(current))}</span>`
      + `<span style="font-size:11px;color:var(--link);">${caret}</span>`;
  };
  pill._setOpen = paintPill;
  paintPill(false);

  // ---- the 300px menu ---------------------------------------------------------
  const openMenu = () => {
    closePanel();
    const menu = document.createElement('div');
    menu.innerHTML = opts.map((o, i) => {
      const isCurrent = o.value === current;
      const ending = !!o.ending;
      const hair = ending
        ? '<div style="height:1px;background:var(--hair);margin:6px 0;"></div>'
        : '';
      const node = isCurrent
        ? '<span style="width:8px;height:8px;flex:none;border-radius:50%;background:var(--brand-blue);margin-top:5px;box-shadow:0 0 0 3px var(--halo);"></span>'
        : `<span style="width:8px;height:8px;flex:none;border:1.5px solid var(--${ending ? 'asp-orange' : 'asp-grey'});border-radius:50%;margin-top:5px;"></span>`;
      const labelStyle = isCurrent
        ? 'font-size:13.5px;font-weight:500;color:var(--anchor);'
        : 'font-size:13.5px;';
      const consStyle = (isCurrent || ending)
        ? 'font-size:11.5px;color:var(--ink-2);'
        : 'font-size:11.5px;color:var(--ink-3);';
      const rowBg = isCurrent ? 'background:var(--nav-hover);' : '';
      return `${hair}<div class="fm-enum-opt" data-i="${i}" style="padding:9px 14px;display:flex;gap:11px;align-items:flex-start;cursor:pointer;${rowBg}">`
        + node
        + '<span style="display:flex;flex-direction:column;gap:2px;">'
        + `<span style="${labelStyle}">${escapeHtml(o.label)}</span>`
        + `<span style="${consStyle}">${escapeHtml(o.consequence || '')}</span>`
        + '</span></div>';
    }).join('');
    Object.assign(menu.style, {
      width: '300px', background: 'var(--brand-white)', border: '1px solid var(--hair)',
      borderRadius: '12px', boxShadow: '0 12px 32px rgba(12,17,57,0.14)', padding: '7px 0', zIndex: '400',
    });
    positionUnder(menu, pill, 300);
    document.body.appendChild(menu);
    registerOpen(menu, pill);
    paintPill(true);

    menu.querySelectorAll('.fm-enum-opt').forEach((el) => {
      const o = opts[+el.dataset.i];
      const isCurrent = o.value === current;
      if (!isCurrent) {
        el.addEventListener('mouseenter', () => { el.style.background = 'var(--nav-hover)'; });
        el.addEventListener('mouseleave', () => { el.style.background = ''; });
      }
      el.addEventListener('click', () => { choose(o); });
    });
  };

  const choose = async (o) => {
    if (o.value === current) { closePanel(); return; } // already the value — nothing to do
    if (o.needsReason || o.ending) { openReason(o); return; } // changes someone else's work
    // plain status change — commit immediately, no confirm
    await onChange(o.value, null);
    current = o.value;
    closePanel();
    paintPill(false);
  };

  // ---- the reason step --------------------------------------------------------
  const openReason = (o) => {
    closePanel();
    const card = document.createElement('div');
    Object.assign(card.style, {
      width: '320px', background: 'var(--brand-white)', border: '1px solid var(--hair)',
      borderRadius: '12px', boxShadow: '0 12px 32px rgba(12,17,57,0.14)', padding: '18px 20px 20px',
      display: 'flex', flexDirection: 'column', gap: '13px', zIndex: '400',
    });
    card.innerHTML =
      `<span style="font-size:15px;font-weight:500;letter-spacing:-0.015em;">${escapeHtml(o.label)}</span>`
      + `<span style="font-size:12.5px;line-height:1.5;color:var(--ink-2);">${escapeHtml(o.consequence || '')}</span>`
      + '<div style="display:flex;flex-direction:column;gap:6px;">'
      + '<span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3);">Reason &#8212; goes on the thread</span>'
      + '<input class="fm-enum-reason" type="text" style="border:1px solid var(--hair-strong);border-radius:8px;padding:10px 12px;font-size:13px;color:var(--anchor);font-family:inherit;box-sizing:border-box;width:100%;" />'
      + '</div>'
      + '<div style="display:flex;gap:10px;padding-top:2px;">'
      + `<button class="fm-enum-confirm" disabled style="height:36px;display:inline-flex;align-items:center;padding:0 18px;border:none;border-radius:8px;background:var(--brand-blue);color:var(--brand-white);font-size:13px;font-weight:500;cursor:default;opacity:0.5;">${escapeHtml(o.label)}</button>`
      + '<button class="fm-enum-cancel" style="height:36px;display:inline-flex;align-items:center;padding:0 14px;border:none;background:none;border-radius:8px;font-size:13px;color:var(--ink-2);cursor:pointer;">Cancel</button>'
      + '</div>';
    positionUnder(card, pill, 320);
    document.body.appendChild(card);
    registerOpen(card, pill);
    paintPill(true);

    const input = card.querySelector('.fm-enum-reason');
    const confirm = card.querySelector('.fm-enum-confirm');
    const cancel = card.querySelector('.fm-enum-cancel');
    const sync = () => {
      const ok = input.value.trim().length > 0;
      confirm.disabled = !ok;
      confirm.style.opacity = ok ? '1' : '0.5';
      confirm.style.cursor = ok ? 'pointer' : 'default';
    };
    input.addEventListener('input', sync);
    confirm.addEventListener('click', async () => {
      const reason = input.value.trim();
      if (!reason) return; // required — keep the button inert
      confirm.disabled = true;
      confirm.style.opacity = '0.5';
      await onChange(o.value, reason);
      current = o.value;
      closePanel();
      paintPill(false);
    });
    cancel.addEventListener('click', () => { closePanel(); });
    setTimeout(() => input.focus(), 0);
  };

  pill.addEventListener('click', () => {
    if (openEl) closePanel(); else openMenu();
  });

  wrap.appendChild(pill);
  container.appendChild(wrap);
}
